import React, { useState, useMemo, useEffect } from "react";
import createPersistedState from "use-persisted-state";
import NavigateBack from "../../components/navigateBack";
import AssessmentScoreGraph from "./assessmentScoreGraph";
import { FormControl, InputLabel, MenuItem, Select } from "@material-ui/core";

const useTokenState = createPersistedState("token");
const usePIDState = createPersistedState("pid");
const useProfileState = createPersistedState("profile");

const getScoreApiUrl = (id, patientId) => {
  switch (id) {
    case "painScore":
      return `/api/v1/pain-score/general/${patientId}?historical=true`;
    case "basdai":
      return `/api/v1/pain-score/basdai/${patientId}?historical=true`;
    case "basfi":
      return `/api/v1/pain-score/basfi/${patientId}?historical=true`;
    case "das28":
      return `/api/v1/pain-score/das28/${patientId}?historical=true`;
    case "cdai":
      return `/api/v1/pain-score/cdai/${patientId}?historical=true`;
    case "basmi":
      return `/api/v1/pain-score/basmi/${patientId}?historical=true`;
    case "rapid3":
      return `/api/v1/pain-score/rapid3/${patientId}?historical=true`;
    case "sledai":
      return `/api/v1/pain-score/sledai/${patientId}?historical=true`;
    case "htSystolic":
      return `/api/v1/vital/systolic/${patientId}`;
    case "htDiastolic":
      return `/api/v1/vital/diastolic/${patientId}`;
    case "spO2":
      return `/api/v1/vital/oxygen/${patientId}`;
    case "pulse":
      return `/api/v1/vital/pulse/${patientId}`;
    case "esr":
      return `/api/v1/tests/parameters/esr/${patientId}`;
    case "crp":
      return `/api/v1/tests/parameters/crp/${patientId}`;
    case "c3":
      return `/api/v1/tests/parameters/c3/${patientId}`;
    default:
      return null;
  }
};

// All available assessment options
const assessmentOptions = [
  {
    id: "painScore",
    label: "Pain Score",
    dataKey: "pain_score",
    max: 10,
    filterKey: "PAIN SCORE",
  },
  {
    id: "basdai",
    label: "BASDAI",
    dataKey: "basdai_score",
    max: 10,
    filterKey: "BASDAI",
  },
  {
    id: "basfi",
    label: "BASFI",
    dataKey: "basfi_score",
    max: 10,
    filterKey: "BASFI",
  },
  {
    id: "basmi",
    label: "BASMI(lin)",
    dataKey: "basmi_score",
    max: 10,
    filterKey: "BASMI",
  },
  {
    id: "das28",
    label: "DAS28",
    dataKey: "das28_score",
    max: 10,
    filterKey: "DAS28",
  },
  {
    id: "cdai",
    label: "CDAI",
    dataKey: "cdai_score",
    max: 80,
    filterKey: "CDAI",
  },
  {
    id: "rapid3",
    label: "RAPID3",
    dataKey: "rapid3_score",
    max: 30,
    filterKey: "RAPID3",
  },
  {
    id: "sledai",
    label: "SLEDAI-2K",
    dataKey: "rapid3_score",
    max: 110,
    filterKey: "SLEDAI",
  },
  {
    id: "htSystolic",
    label: "HT Systolic",
    dataKey: "ht_systolic_score",
    max: 200,
    filterKey: "HT SYSTOLIC",
  },
  {
    id: "htDiastolic",
    label: "HT Diastolic",
    dataKey: "ht_diastolic_score",
    max: 120,
    filterKey: "HT DIASTOLIC",
  },
  {
    id: "spO2",
    label: "SpO2",
    dataKey: "spo2_score",
    max: 100,
    filterKey: "SpO2",
  },
  {
    id: "pulse",
    label: "Pulse",
    dataKey: "pulse_score",
    max: 200,
    filterKey: "Pulse",
  },
  { id: "esr", label: "ESR", dataKey: "esr_score", max: 100, filterKey: "ESR" },
  { id: "crp", label: "CRP", dataKey: "crp_score", max: 100, filterKey: "CRP" },
  { id: "c3", label: "C3", dataKey: "c3_score", max: 100, filterKey: "C3" },
];

function formatDate(isoTimestamp) {
  if (typeof isoTimestamp !== "string" || !isoTimestamp.includes("T")) {
    return "Invalid Date";
  }

  // Extract YYYY, MM, DD from the string
  const [year, month, day] = isoTimestamp.split("T")[0].split("-");

  // Convert month number to short name
  const monthName = new Date(`${year}-${month}-01`).toLocaleString("default", {
    month: "short",
  });

  return `${day} ${monthName}, ${year.slice(-2)}`;
}
const ScoreCharts = () => {
  const [token] = useTokenState();
  const [pid] = usePIDState();
  const [profile] = useProfileState();

  const [isLoading, setLoading] = useState(true);
  const [chartData, setChartData] = useState([]);

  // Filter options based on allowed scores
  const filteredOptions = useMemo(() => {
    const allowedSet = new Set(
      profile?.availableCharts?.map((s) => s.toLowerCase()) || []
    );

    const result = assessmentOptions.filter((opt) =>
      allowedSet.has(opt.filterKey.toLowerCase())
    );

    // If no options allowed, return first "Pain Score" option
    return result.length > 0 ? result : [assessmentOptions[0]];
  }, [profile?.availableCharts]);

  const [selectedId, setSelectedId] = useState(
    filteredOptions.length > 0 ? filteredOptions[0].id : ""
  );

  const selectedOption = filteredOptions.find((opt) => opt.id === selectedId);

  const fetchScoreData = async (option) => {
    setLoading(true);
    try {
      const res = await fetch(getScoreApiUrl(option.id, pid), {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!res.ok) throw new Error("Network error");

      const data = await res.json();

      setChartData(data?.historical, pid);
    } catch (err) {
      console.error("Fetch failed", err);
      setChartData([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (selectedOption && pid && token) {
      fetchScoreData(selectedOption);
    }
  }, [selectedOption, pid, token]);

  return (
    <>
      <NavigateBack title="Score Charts" />

      {isLoading ? (
        <div className="body_content">
          <div className="inner_body py-5">
            <div
              style={{
                margin: "auto",
                display: "flex",
                justifyContent: "center",
              }}
              id="loader"
            ></div>
          </div>
        </div>
      ) : (
        <div className="body_content">
          <div className="inner_body ">
            <FormControl fullWidth margin="normal">
              <InputLabel id="score-select-label">Select Score</InputLabel>
              <Select
                labelId="score-select-label"
                id="score-select"
                value={selectedId}
                onChange={(e) => setSelectedId(e.target.value)}
                label="Select Score" // Must match InputLabel text
              >
                {filteredOptions.map((opt) => (
                  <MenuItem key={opt.id} value={opt.id}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <div className="">
              {selectedOption && (
                <div>
                  <div style={{ height: 450, width: "100%" }}>
                    <AssessmentScoreGraph
                      patientsAssessmentScoreHistory={chartData}
                      label={selectedOption.label}
                      maxValue={selectedOption.max}
                    />
                  </div>
                  {!chartData ? (
                    <div className="container px-4 mt-2">
                      <div
                        className="text-center"
                        style={{ color: "#000000B2" }}
                      >
                        No score history available.
                      </div>
                    </div>
                  ) : (
                    <div className="container  mt-2" style={{ height: "100%" }}>
                      <div
                        className="list-group"
                        style={{ height: "100%", overflowY: "auto" }}
                      >
                        {chartData?.map((item, index) => (
                          <div
                            key={index}
                            className="list-group-item border-bottom py-2 d-flex justify-content-between"
                            style={{
                              borderBottom: "1px solid #e0e0e0",
                              borderTop: "none",
                              borderLeft: "none",
                              borderRight: "none",
                              color: "#000000B2",
                              fontSize: "14px",
                              padding: "0 0",
                            }}
                          >
                            <span>{item.value}</span>
                            <span>{formatDate(item.time)}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ScoreCharts;
